import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { getCurrentUser, login as loginRequest, logout as logoutRequest, type CurrentUser } from '../../../types'
import { actionApiCall, formSubmit, type ApiFailure } from '../api/actionApiCall'
import { clearCachedUser, readCachedUser, writeCachedUser } from '../offline/cachedUser'

type AuthContextValue = {
    user: CurrentUser | undefined
    isLoading: boolean
    login: (login: string, password: string) => Promise<{ ok: boolean; fieldErrors?: Record<string, string> }>
    logout: () => Promise<void>
    /** Aktualizuje profil v paměti (např. po změně hesla). */
    setUser: (user: CurrentUser) => void
    refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const CURRENT_USER_KEY = ['auth', 'me']

export function AuthProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient()

    const { data, isLoading } = useQuery({
        queryKey: CURRENT_USER_KEY,
        queryFn: async () => {
            let failure: ApiFailure | undefined

            // 401 je u nepřihlášeného očekávaný stav, ne chyba k zobrazení.
            const user = await actionApiCall(() => getCurrentUser(), {
                silent: true,
                onError: (error) => {
                    failure = error
                    return true
                },
            })

            if (user) {
                writeCachedUser(user)
                return user
            }

            // Výpadek sítě (status 0) neznamená odhlášení - použije se poslední
            // známý profil, aby aplikace zůstala offline použitelná.
            if (failure?.status === 0) {
                const cached = readCachedUser()
                if (cached) {
                    return cached
                }
            }

            clearCachedUser()
            return null
        },
        staleTime: 5 * 60 * 1000,
        retry: false,
    })

    const login = useCallback(
        async (loginName: string, password: string) => {
            const { data: user, fieldErrors } = await formSubmit(() => loginRequest({ body: { login: loginName, password } }))
            if (!user) {
                return { ok: false, fieldErrors }
            }
            writeCachedUser(user)
            queryClient.setQueryData(CURRENT_USER_KEY, user)
            return { ok: true }
        },
        [queryClient],
    )

    const logout = useCallback(async () => {
        await actionApiCall(() => logoutRequest(), { silent: true })
        clearCachedUser()
        queryClient.setQueryData(CURRENT_USER_KEY, null)
        // Po odhlášení nesmí v paměti zůstat data předchozího uživatele.
        queryClient.clear()
    }, [queryClient])

    const setUser = useCallback(
        (user: CurrentUser) => {
            writeCachedUser(user)
            queryClient.setQueryData(CURRENT_USER_KEY, user)
        },
        [queryClient],
    )

    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY })
    }, [queryClient])

    const value = useMemo<AuthContextValue>(
        () => ({ user: data ?? undefined, isLoading, login, logout, setUser, refresh }),
        [data, isLoading, login, logout, setUser, refresh],
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth lze použít jen uvnitř AuthProvider')
    }
    return context
}
